/**
 * Typed fetch client for the FormaChat API.
 *
 * Every request goes to a relative URL, so the dashboard works unchanged behind
 * nginx in production and behind the Vite proxy in development. The bearer
 * token is attached from module state rather than from a cookie: an ambient
 * credential could be replayed by any other site, a header cannot.
 */

import type {
  AuthConfig,
  Bot,
  BotSettings,
  BotSummary,
  ContactDetail,
  ContactFilters,
  ContactListResponse,
  ConversationDetail,
  ConversationListResponse,
  ChatMessage,
  CurrentUser,
  DataSourceDetail,
  DataSourceListResponse,
  HealthResponse,
  IngestSummary,
  LoginResponse,
  RetrievalResponse,
  ClassifyResult,
  ImportSummary,
  NetsuiteQueueResponse,
  NetsuitePushResult,
  ReportDetail,
  ReportListResponse,
  AnalyticsResponse,
  EmbedInstructions,
  ExclusionPreview,
  WebsiteScan,
  CorpusSections,
  SectionEditResult,
  SupportedFileTypes,
  Suggestion,
  SuggestionListResponse,
  SuggestionGenerateResult,
  BuildInfo,
  SystemCheckRun,
  LatencyReport,
  AiProviderSettings,
  AiProviderTestResult,
  AiProviderUpdate,
  EmailSettings,
  EmailSettingsUpdate,
  EmailTestResult,
} from './types';

const TOKEN_STORAGE_KEY = 'formachat.token';

/** An API call that returned a non-2xx status. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session is missing or expired and the user must log in. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

let token: string | null = localStorage.getItem(TOKEN_STORAGE_KEY);

/** Store the bearer token, or clear it when `next` is null. */
export function setToken(next: string | null): void {
  token = next;
  if (next) {
    localStorage.setItem(TOKEN_STORAGE_KEY, next);
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

/** Return the stored bearer token, if any. */
export function getToken(): string | null {
  return token;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `${path}?${encoded}` : path;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (!response.ok) {
    // FastAPI puts the reason in `detail`; fall back to the status text when a
    // proxy or an unhandled error returns something else.
    let detail = response.statusText;
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) detail = payload.detail;
    } catch {
      // Body was not JSON. The status text is the best available message.
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function attachmentPath(
  slug: string,
  conversationId: string,
  attachmentId: string,
): string {
  return `/api/bots/${slug}/conversations/${conversationId}/attachments/${attachmentId}`;
}

export const api = {
  // -- Session ---------------------------------------------------------------

  async login(email: string, password: string): Promise<LoginResponse> {
    const result = await request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    setToken(result.access_token);
    return result;
  },

  logout(): void {
    setToken(null);
  },

  currentUser: () => request<CurrentUser>('/api/auth/me'),
  authConfig: () => request<AuthConfig>('/api/auth/config'),

  /** Full-page navigation to Microsoft; returns to `nextPath` once signed in. */
  startSso(nextPath: string): void {
    window.location.assign(buildUrl('/api/auth/sso/login', { next: nextPath }));
  },
  health: () => request<HealthResponse>('/api/health'),

  // -- Bots ------------------------------------------------------------------

  listBots: () => request<BotSummary[]>('/api/bots'),
  getBot: (slug: string) => request<Bot>(`/api/bots/${slug}`),

  createBot: (payload: {
    slug: string;
    name: string;
    site_url: string;
    allowed_origins: string[];
  }) => request<Bot>('/api/bots', { method: 'POST', body: payload }),

  updateBot: (
    slug: string,
    payload: Partial<{
      name: string;
      site_url: string;
      allowed_origins: string[];
      is_active: boolean;
    }>,
  ) => request<Bot>(`/api/bots/${slug}`, { method: 'PATCH', body: payload }),

  /**
   * Save part of a bot's settings.
   *
   * Send only the panel being saved: the backend leaves unsupplied fields
   * alone, so a partial payload cannot reset settings owned by another panel.
   */
  saveBotSettings: (slug: string, payload: Partial<BotSettings>) =>
    request<Bot>(`/api/bots/${slug}/settings`, { method: 'PUT', body: payload }),

  // -- Data sources ----------------------------------------------------------

  listDataSources: (slug: string, search?: string) =>
    request<DataSourceListResponse>(`/api/bots/${slug}/data-sources`, {
      query: { search },
    }),

  getDataSource: (slug: string, id: string) =>
    request<DataSourceDetail>(`/api/bots/${slug}/data-sources/${id}`),

  addUrlSource: (slug: string, url: string) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/url`, {
      method: 'POST',
      body: { url },
    }),

  addUrlListSource: (
    slug: string,
    payload: { sitemap_url?: string; urls?: string[] },
  ) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/url-list`, {
      method: 'POST',
      body: payload,
    }),

  addTextSource: (slug: string, name: string, content: string) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/text`, {
      method: 'POST',
      body: { name, content },
    }),

  addQaSource: (slug: string, question: string, answer: string) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/qa`, {
      method: 'POST',
      body: { question, answer },
    }),

  /**
   * Train on an entire website from its homepage.
   *
   * The backend finds the site's sitemap, or walks its links when there is
   * none, and creates one data source per page.
   */
  addWebsiteSource: (
    slug: string,
    url: string,
    options: { exclude?: string[]; saveExclusions?: boolean } = {},
  ) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/website`, {
      method: 'POST',
      body: {
        url,
        exclude: options.exclude ?? [],
        save_exclusions: options.saveExclusions ?? false,
      },
    }),

  /**
   * List a website's sections without training on anything.
   *
   * Reads the site's sitemap and groups what it finds by top-level slug, so
   * the operator can tick the sections to leave out instead of guessing what
   * the site calls them.
   */
  scanWebsite: (slug: string, url: string, exclude: string[] = []) =>
    request<WebsiteScan>(`/api/bots/${slug}/data-sources/website/scan`, {
      method: 'POST',
      body: { url, exclude },
    }),

  addYoutubeSource: (slug: string, url: string) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/youtube`, {
      method: 'POST',
      body: { url },
    }),

  supportedFileTypes: (slug: string) =>
    request<SupportedFileTypes>(`/api/bots/${slug}/data-sources/file/supported-types`),

  /** Upload a training document. Multipart, so it bypasses `request`. */
  async addFileSource(slug: string, file: File): Promise<IngestSummary> {
    const form = new FormData();
    form.append('file', file);

    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`/api/bots/${slug}/data-sources/file`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!response.ok) {
      let detail = response.statusText;
      try {
        const payload = (await response.json()) as { detail?: string };
        if (payload.detail) detail = payload.detail;
      } catch {
        // Not JSON; the status text is the best message available.
      }
      throw new ApiError(response.status, detail);
    }
    return (await response.json()) as IngestSummary;
  },

  /** The website sections this bot is trained on, largest first. */
  listSections: (slug: string) =>
    request<CorpusSections>(`/api/bots/${slug}/data-sources/sections`),

  /**
   * Turn website sections off or back on.
   *
   * Removing deletes the section's trained pages and adds it to the bot's
   * saved exclusions. Restoring only lifts the exclusion — deleted pages have
   * to be crawled again.
   */
  editSections: (slug: string, remove: string[], restore: string[]) =>
    request<SectionEditResult>(`/api/bots/${slug}/data-sources/sections`, {
      method: 'PUT',
      body: { remove, restore },
    }),

  getExclusions: (slug: string) =>
    request<{ exclusions: string[] }>(`/api/bots/${slug}/data-sources/exclusions`),

  saveExclusions: (slug: string, exclusions: string[]) =>
    request<ExclusionPreview>(`/api/bots/${slug}/data-sources/exclusions`, {
      method: 'PUT',
      body: { exclusions },
    }),

  previewExclusions: (slug: string, exclusions: string[]) =>
    request<ExclusionPreview>(`/api/bots/${slug}/data-sources/exclusions/preview`, {
      method: 'POST',
      body: { exclusions },
    }),

  retrainDataSource: (slug: string, id: string) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/${id}/retrain`, {
      method: 'POST',
    }),

  retrainAllDataSources: (slug: string) =>
    request<IngestSummary>(`/api/bots/${slug}/data-sources/retrain-all`, {
      method: 'POST',
    }),

  deleteDataSource: (slug: string, id: string) =>
    request<void>(`/api/bots/${slug}/data-sources/${id}`, { method: 'DELETE' }),

  testRetrieval: (slug: string, query: string) =>
    request<RetrievalResponse>(`/api/bots/${slug}/data-sources/retrieval-test`, {
      method: 'POST',
      body: { query },
    }),

  // -- Inbox -----------------------------------------------------------------

  listConversations: (
    slug: string,
    options: {
      status?: 'open' | 'closed';
      search?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    request<ConversationListResponse>(`/api/bots/${slug}/conversations`, {
      query: {
        status: options.status,
        search: options.search,
        limit: options.limit ?? 100,
        offset: options.offset,
      },
    }),

  getConversation: (slug: string, id: string) =>
    request<ConversationDetail>(`/api/bots/${slug}/conversations/${id}`),

  /** Download a file the visitor sent; served from Azure through the API. */
  downloadAttachment: (
    slug: string,
    conversationId: string,
    attachmentId: string,
    filename: string,
  ) => api.downloadAuthed(attachmentPath(slug, conversationId, attachmentId), filename),

  /** The file itself, for showing an image or PDF in the Inbox. */
  attachmentBlob: (slug: string, conversationId: string, attachmentId: string) =>
    api.fetchAuthedBlob(attachmentPath(slug, conversationId, attachmentId)),

  sendAgentMessage: (slug: string, id: string, content: string) =>
    request<ChatMessage>(`/api/bots/${slug}/conversations/${id}/messages`, {
      method: 'POST',
      body: { content },
    }),

  closeConversation: (slug: string, id: string) =>
    request<ConversationDetail>(`/api/bots/${slug}/conversations/${id}/close`, {
      method: 'POST',
    }),

  reopenConversation: (slug: string, id: string) =>
    request<ConversationDetail>(`/api/bots/${slug}/conversations/${id}/reopen`, {
      method: 'POST',
    }),

  setInternalNote: (slug: string, id: string, note: string | null) =>
    request<ConversationDetail>(`/api/bots/${slug}/conversations/${id}/note`, {
      method: 'PUT',
      body: { internal_note: note },
    }),

  // -- Reports ---------------------------------------------------------------

  listReports: (slug: string) =>
    request<ReportListResponse>(`/api/bots/${slug}/reports`),

  getReport: (slug: string, id: string) =>
    request<ReportDetail>(`/api/bots/${slug}/reports/${id}`),

  runReport: (
    slug: string,
    payload: { type: 'historical' | 'recurring'; send_email: boolean },
  ) =>
    request<ReportDetail>(`/api/bots/${slug}/reports/run`, {
      method: 'POST',
      body: payload,
    }),

  classifyPending: (slug: string) =>
    request<ClassifyResult>(`/api/bots/${slug}/reports/classify`, {
      method: 'POST',
    }),

  netsuiteQueue: (slug: string) =>
    request<NetsuiteQueueResponse>(`/api/bots/${slug}/reports/netsuite/queue`),

  approveLead: (slug: string, classificationId: string) =>
    request<{ pushed: boolean; dry_run: boolean; detail: string }>(
      `/api/bots/${slug}/reports/netsuite/${classificationId}/approve`,
      { method: 'POST' },
    ),

  dismissLead: (slug: string, classificationId: string) =>
    request<{ status: string }>(
      `/api/bots/${slug}/reports/netsuite/${classificationId}/dismiss`,
      { method: 'POST' },
    ),

  /** Upload a LiveChatAI CSV export. Multipart, so it bypasses `request`. */
  async importCsv(
    slug: string,
    kind: 'conversations' | 'contacts',
    file: File,
  ): Promise<ImportSummary> {
    const form = new FormData();
    form.append('file', file);

    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`/api/bots/${slug}/reports/import/${kind}`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!response.ok) {
      let detail = response.statusText;
      try {
        const payload = (await response.json()) as { detail?: string };
        if (payload.detail) detail = payload.detail;
      } catch {
        // Not JSON; the status text is the best message available.
      }
      throw new ApiError(response.status, detail);
    }
    return (await response.json()) as ImportSummary;
  },

  // -- Analytics & embed -----------------------------------------------------

  analytics: (slug: string, start: string, end: string) =>
    request<AnalyticsResponse>(`/api/bots/${slug}/analytics`, {
      query: { start, end },
    }),

  embedInstructions: (slug: string) =>
    request<EmbedInstructions>(`/api/bots/${slug}/embed`),

  /** Fetch a file that needs the Authorization header. */
  async fetchAuthedBlob(path: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(path, { headers });
    if (!response.ok) throw new ApiError(response.status, response.statusText);
    return response.blob();
  },

  /** Download a file that needs the Authorization header. */
  async downloadAuthed(path: string, filename: string): Promise<void> {
    const url = URL.createObjectURL(await this.fetchAuthedBlob(path));
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  },

  downloadAnalyticsCsv(slug: string, start: string, end: string): Promise<void> {
    return this.downloadAuthed(
      buildUrl(`/api/bots/${slug}/analytics/export.csv`, { start, end }),
      `${slug}-analytics.csv`,
    );
  },

  downloadWordPressPlugin(slug: string): Promise<void> {
    return this.downloadAuthed(
      `/api/bots/${slug}/embed/wordpress-plugin.zip`,
      `formachat-wp-${slug}.zip`,
    );
  },

  // -- Contacts --------------------------------------------------------------

  listContacts: (slug: string, filters: ContactFilters = {}, limit = 100) =>
    request<ContactListResponse>(`/api/bots/${slug}/contacts`, {
      query: { ...filters, limit },
    }),

  /**
   * Load one contact with its conversations and full transcripts.
   *
   * Opening a contact marks it Read. Pass `markRead: false` to look without
   * clearing the New marker.
   */
  getContact: (slug: string, id: string, markRead = true) =>
    request<ContactDetail>(`/api/bots/${slug}/contacts/${id}`, {
      query: { mark_read: markRead },
    }),

  updateContact: (
    slug: string,
    id: string,
    payload: Partial<{
      name: string | null;
      email: string | null;
      phone: string | null;
      company: string | null;
      country: string | null;
      postal_code: string | null;
      type: 'lead' | 'customer';
      status: 'new' | 'read' | 'lead_created';
    }>,
  ) =>
    request<ContactDetail>(`/api/bots/${slug}/contacts/${id}`, {
      method: 'PATCH',
      body: payload,
    }),

  addContactToNetsuite: (slug: string, id: string) =>
    request<NetsuitePushResult>(`/api/bots/${slug}/contacts/${id}/netsuite`, {
      method: 'POST',
    }),

  deleteContact: (slug: string, id: string) =>
    request<void>(`/api/bots/${slug}/contacts/${id}`, { method: 'DELETE' }),

  // -- AI provider -----------------------------------------------------------

  aiProvider: () => request<AiProviderSettings>('/api/settings/ai-provider'),

  /** Verified against the vendor before anything is stored; 422 carries why. */
  updateAiProvider: (update: AiProviderUpdate) =>
    request<AiProviderSettings>('/api/settings/ai-provider', {
      method: 'PUT',
      body: update,
    }),

  /** Delete every dashboard override, so the `.env` values are in force. */
  resetAiProvider: () =>
    request<AiProviderSettings>('/api/settings/ai-provider', { method: 'DELETE' }),

  testAiProvider: () =>
    request<AiProviderTestResult>('/api/settings/ai-provider/test', {
      method: 'POST',
    }),

  // -- Email -----------------------------------------------------------------

  emailSettings: () => request<EmailSettings>('/api/settings/email'),

  /** Verified (SMTP login, or a signed ACS lookup) before anything is stored. */
  updateEmailSettings: (update: EmailSettingsUpdate) =>
    request<EmailSettings>('/api/settings/email', { method: 'PUT', body: update }),

  resetEmailSettings: () =>
    request<EmailSettings>('/api/settings/email', { method: 'DELETE' }),

  /** Sends a real email; to the signed-in admin when `to` is omitted. */
  testEmailSettings: (to?: string) =>
    request<EmailTestResult>('/api/settings/email/test', {
      method: 'POST',
      body: to ? { to } : {},
    }),

  // -- System check ----------------------------------------------------------

  /** The stored result of the last run, or null before the first. */
  lastSystemCheck: () => request<SystemCheckRun | null>('/api/system-check'),

  runSystemCheck: () =>
    request<SystemCheckRun>('/api/system-check', { method: 'POST' }),

  /** The GitHub commit the live deployment was built from. */
  buildInfo: () => request<BuildInfo>('/api/system-check/build'),

  // -- Latency ---------------------------------------------------------------

  /** Recent message traces and per-step statistics; every bot when no slug. */
  latency: (params: { slug?: string; hours?: number; limit?: number }) =>
    request<LatencyReport>('/api/latency', { query: params }),

  // -- AI suggestions --------------------------------------------------------

  listSuggestions: (slug: string, status?: 'pending' | 'added' | 'dismissed') =>
    request<SuggestionListResponse>(`/api/bots/${slug}/suggestions`, {
      query: { status },
    }),

  generateSuggestions: (slug: string) =>
    request<SuggestionGenerateResult>(`/api/bots/${slug}/suggestions/generate`, {
      method: 'POST',
    }),

  acceptSuggestion: (slug: string, id: string, question: string, answer: string) =>
    request<Suggestion>(`/api/bots/${slug}/suggestions/${id}/accept`, {
      method: 'POST',
      body: { question, answer },
    }),

  dismissSuggestion: (slug: string, id: string) =>
    request<Suggestion>(`/api/bots/${slug}/suggestions/${id}/dismiss`, {
      method: 'POST',
    }),

  deleteSuggestion: (slug: string, id: string) =>
    request<void>(`/api/bots/${slug}/suggestions/${id}`, { method: 'DELETE' }),

  /**
   * Download the filtered contacts as a CSV file.
   *
   * Fetched rather than linked: the export endpoint needs the Authorization
   * header, and a plain `<a href>` cannot send one.
   */
  async downloadContactsCsv(slug: string, filters: ContactFilters = {}): Promise<void> {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(
      buildUrl(`/api/bots/${slug}/contacts/export.csv`, { ...filters }),
      { headers },
    );
    if (!response.ok) {
      throw new ApiError(response.status, response.statusText);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = `${slug}-contacts.csv`;
      link.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};
